import { FC } from 'react';

import CtaButton from '@components/quiz/shared/CtaButton';
import SquadPitch from '@components/manual-team-entry/squad-pitch';
import { useReadiness } from '@components/quiz/ReadinessProvider';
import { useSquadPortraits } from '@components/quiz/ReadinessProvider/utils/useSquadPortraits';
import type { SanityReadinessCaptainBlock } from '@lib/sanity/types';

type CaptainBlockProps = SanityReadinessCaptainBlock & {
  onNext: () => void;
};

/**
 * Step 4: captain pick. Feedback NEVER names the AI's pick (spec 6.7) —
 * a match shows "Strong captain call", a mismatch shows "Bold call".
 * Skippable; the AI captain badge is not shown mid-flow.
 */
const CaptainBlock: FC<CaptainBlockProps> = ({
  pitchInstruction,
  matchFeedbackHeading,
  matchFeedbackBody,
  mismatchFeedbackHeading,
  mismatchFeedbackBody,
  skipLinkText,
  ctaText,
  onNext
}) => {
  const { state, actions } = useReadiness();
  const { pitchTeam } = useSquadPortraits(state.team);

  const handlePlayerTap = (playerId: string) => {
    const player = state.team.find(
      (member) => member.player?.id === playerId
    )?.player;
    if (player) actions.selectCaptain(player);
  };

  const handleSkip = () => {
    actions.skipCaptain();
    onNext();
  };

  const comparison = state.captainComparison;
  const isMatch = comparison?.isUserMatchingAi ?? false;

  return (
    <section className="flex flex-col items-center px-4 pb-16 pt-6">
      {pitchInstruction && (
        <p className="mb-3 max-w-sm text-center font-body text-xs font-semibold uppercase tracking-widest text-neutral-500">
          {pitchInstruction}
        </p>
      )}

      <SquadPitch
        team={pitchTeam}
        onPlayerTap={handlePlayerTap}
        selectedPlayerId={state.selectedCaptain?.id ?? null}
        captainId={state.selectedCaptain?.id ?? null}
      />

      <div className="w-full max-w-sm">
        {/* Feedback card — never names the AI pick */}
        {comparison && (
          <div
            data-cy="readiness-captain-feedback"
            className={`mt-5 rounded-xl border px-5 py-4 ${
              isMatch
                ? 'border-success-200 bg-success-50'
                : 'border-warning-200 bg-warning-50'
            }`}
          >
            <p className="font-body text-sm text-neutral-700">
              <span
                className={`font-bold ${
                  isMatch ? 'text-success-700' : 'text-warning-700'
                }`}
              >
                {isMatch
                  ? matchFeedbackHeading ?? 'Strong captain call.'
                  : mismatchFeedbackHeading ?? 'Bold call.'}
              </span>{' '}
              {isMatch
                ? matchFeedbackBody ??
                  'Our AI rates this among the top captain options this week.'
                : mismatchFeedbackBody ??
                  'Our AI is backing a different captain this week, projected a few points higher.'}
            </p>
          </div>
        )}

        <div className="mt-5">
          <CtaButton
            onClick={onNext}
            disabled={!state.selectedCaptain}
            dataCy="readiness-captain-cta"
          >
            {ctaText ?? 'Next'}
          </CtaButton>
          {skipLinkText && (
            <button
              type="button"
              onClick={handleSkip}
              data-cy="readiness-captain-skip"
              className="mt-3 w-full text-center font-body text-sm font-semibold text-primary-500 transition-colors hover:text-primary-600"
            >
              {skipLinkText}
            </button>
          )}
        </div>
      </div>
    </section>
  );
};

export default CaptainBlock;
